import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Header } from '../components/layout/Header';
import { OfferDetailsView } from '../components/gigs/my-gigs/offers/OfferDetailsView';
import { useOffer } from '../api/offers';
import { offerPaymentPollInterval } from '../components/gigs/my-gigs/offers/paymentLinks';
import { useUserStore } from '../store/useUserStore';
import { FiAlertCircle } from 'react-icons/fi';
import { Button } from '../components/ui/Button';
import { parseApiError } from '../utils/apiError';

export const Route = createFileRoute('/offers/$offerId/shared-link')({
  component: SharedOfferDetailPage,
});

function SharedOfferDetailPage() {
  const { offerId } = Route.useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useUserStore();
  const currentUserId = user?.id != null ? String(user.id) : '';
  const currentUserEmail = (user?.email || '').toLowerCase();
  const {
    data: offer,
    isLoading,
    isError,
    error,
    refetch,
  } = useOffer(offerId, {
    refetchInterval: (data) => offerPaymentPollInterval(data, currentUserId, currentUserEmail),
  });
  const status = error ? parseApiError(error).status : undefined;
  const isAuthError = status === 401 || status === 403;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black text-white pb-28">
        <Header />
        <main className="mx-auto max-w-[1360px] px-4 sm:px-6 lg:px-8 pt-[calc(var(--header-height,5.5rem)+1.75rem)] flex flex-col items-center justify-center min-h-[50vh] text-center">
          <p className="text-sm text-zinc-400">Loading offer…</p>
        </main>
      </div>
    );
  }

  if (isError || !offer) {
    // Soft Sign In only when the API rejects as auth — not for generic not-found while logged out
    const showSignIn = isAuthError && !isAuthenticated;
    return (
      <div className="min-h-screen bg-black text-white pb-28">
        <Header />
        <main className="mx-auto max-w-[1360px] px-4 sm:px-6 lg:px-8 pt-[calc(var(--header-height,5.5rem)+1.75rem)] flex flex-col items-center justify-center min-h-[50vh] text-center">
          <FiAlertCircle size={48} className="text-zinc-600 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">
            {showSignIn ? 'Sign in to view this offer' : 'Offer not found'}
          </h2>
          <p className="text-sm text-zinc-400 mb-6">
            {showSignIn
              ? 'This offer link requires you to sign in first. You will be returned here after login.'
              : 'The requested offer could not be found or has expired.'}
          </p>
          <div className="flex items-center gap-3">
            {showSignIn ? (
              <Button
                onClick={() =>
                  navigate({
                    to: '/sign-in',
                    search: { redirect: `/offers/${offerId}/shared-link` },
                  })
                }
                size="lg"
                className="rounded-full px-8"
              >
                Sign In
              </Button>
            ) : (
              <Button onClick={() => refetch()} size="lg" className="rounded-full px-8">
                Retry
              </Button>
            )}
            <Button onClick={() => navigate({ to: '/' })} size="lg" className="rounded-full px-8">
              Back to Home
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pb-28">
      <Header />
      <main className="mx-auto max-w-[1360px] px-4 sm:px-6 lg:px-8 pt-[calc(var(--header-height,5.5rem)+1.75rem)]">
        <OfferDetailsView
          offer={offer}
          onBack={() => navigate({ to: '/' })}
          onStatusChange={() => {
            void refetch();
          }}
        />
      </main>
    </div>
  );
}
